import React, { useState, useEffect } from 'react';
import axios from 'axios';
// import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import '../App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SpotifyFullViewModal = ({ 
  isOpen, 
  onClose, 
  activePlaylistForAdding, 
  addTrackToPlaylist, 
  onTrackAdded,
  // Crate context props
  isAddingToCrate,
  targetCrateId,
  targetCrateName,
  onTrackAddedToCrate,
  onReturnToCrate
}) => {
  const [playlists, setPlaylists] = useState([]);
  const [filteredPlaylists, setFilteredPlaylists] = useState([]);
  const [selectedPlaylist, setSelectedPlaylist] = useState(null);
  const [playlistTracks, setPlaylistTracks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [spotifyToken, setSpotifyToken] = useState(null);
  const [addingTrackId, setAddingTrackId] = useState(null);

  // Function to refresh Spotify token
  const refreshSpotifyToken = async () => {
    const refreshToken = localStorage.getItem('spotify_refresh_token');
    if (!refreshToken) {
      console.error('No refresh token available');
      return null;
    }
    
    try {
      const response = await axios.post(`${API}/spotify/auth/refresh`, null, {
        params: { refresh_token: refreshToken }
      });
      
      if (response.data.access_token) {
        localStorage.setItem('spotify_access_token', response.data.access_token);
        setSpotifyToken(response.data.access_token);
        return response.data.access_token;
      }
    } catch (err) {
      console.error('Failed to refresh Spotify token:', err);
      // Clear tokens if refresh fails
      localStorage.removeItem('spotify_access_token');
      localStorage.removeItem('spotify_refresh_token');
    }
    return null;
  };

  useEffect(() => {
    if (isOpen) {
      const token = localStorage.getItem('spotify_access_token');
      if (token) {
        setSpotifyToken(token);
        fetchPlaylists(token);
      }
    }
  }, [isOpen]);

  useEffect(() => {
    // Filter playlists based on search
    if (searchQuery.trim()) {
      setFilteredPlaylists(
        playlists.filter(p => 
          p.name.toLowerCase().includes(searchQuery.toLowerCase())
        )
      );
    } else {
      setFilteredPlaylists(playlists);
    }
  }, [searchQuery, playlists]);

  const fetchPlaylists = async (token, isRetry = false) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/spotify/user/playlists`, {
        params: { access_token: token }
      });
      setPlaylists(response.data.playlists || []);
    } catch (err) {
      console.error('Failed to fetch playlists:', err);
      // Check if it's a 401 (token expired) and try to refresh
      if (err.response?.status === 401 && !isRetry) {
        console.log('Token expired, attempting refresh...');
        const newToken = await refreshSpotifyToken();
        if (newToken) {
          // Retry with new token
          return fetchPlaylists(newToken, true);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchPlaylistDetails = async (playlistId, token = spotifyToken, isRetry = false) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/spotify/playlist/${playlistId}/tracks`, {
        params: {
          access_token: token,
          limit: 50,
          offset: 0
        }
      });
      setPlaylistTracks(response.data.tracks || []);
    } catch (err) {
      console.error('Failed to fetch playlist details:', err);
      // Check if it's a 401 (token expired) and try to refresh
      if (err.response?.status === 401 && !isRetry) {
        console.log('Token expired, attempting refresh...');
        const newToken = await refreshSpotifyToken();
        if (newToken) {
          // Retry with new token
          return fetchPlaylistDetails(playlistId, newToken, true);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePlaylistClick = (playlist) => {
    setSelectedPlaylist(playlist);
    fetchPlaylistDetails(playlist.id);
  };

  // Drag and drop temporarily disabled
  // const handleDragEnd = (result) => {
  //   if (!result.destination) return;
  //   const items = Array.from(filteredPlaylists);
  //   const [reorderedItem] = items.splice(result.source.index, 1);
  //   items.splice(result.destination.index, 0, reorderedItem);
  //   setFilteredPlaylists(items);
  //   setPlaylists(items);
  // };

  const closeDetailView = () => {
    setSelectedPlaylist(null);
    setPlaylistTracks([]);
  };

  const handleAddTrack = async (track) => {
    if (!activePlaylistForAdding || !addTrackToPlaylist) {
      alert('No active playlist selected for adding tracks');
      return;
    }

    setAddingTrackId(track.id);
    
    try {
      // Format track data to match backend AddTrackToPlaylist model
      const trackData = {
        track_id: track.id || track.uri || `spotify_${Date.now()}`,
        title: track.name || 'Unknown Track',
        artist: track.artist || 'Unknown Artist',
        source: 'spotify',
        artwork: track.album_art || null,
        preview_url: track.preview_url || null,
        spotify_uri: track.uri || null,
        duration_ms: track.duration_ms || 0
      };

      console.log('Adding track to playlist:', trackData);
      console.log('Track spotify_uri:', track.uri);
      
      await addTrackToPlaylist(activePlaylistForAdding.id, trackData);
      
      // Show success feedback
      alert(`✅ "${track.name}" added to ${activePlaylistForAdding.name}!\n\n🎵 Will play via Spotify Premium account.`);
      
      // Call the onTrackAdded callback to navigate back to playlist
      if (onTrackAdded) {
        onTrackAdded();
      }
    } catch (error) {
      console.error('Error adding track:', error);
      // Better error message handling
      let errorMsg = 'Unknown error';
      if (error.response?.data?.detail) {
        errorMsg = error.response.data.detail;
      } else if (error.message) {
        errorMsg = error.message;
      } else if (typeof error === 'string') {
        errorMsg = error;
      } else {
        errorMsg = JSON.stringify(error);
      }
      alert(`❌ Failed to add track: ${errorMsg}`);
    } finally {
      setAddingTrackId(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="jam-modal-overlay" 
      onClick={onClose}
      style={{ zIndex: 100000 }}
    >
      {/* Gradient Border Wrapper */}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          padding: '3px',
          borderRadius: '24px',
          background: 'linear-gradient(90deg, #00ffff, #ff00ff, #00ffff)',
          backgroundSize: '200% 100%',
          animation: 'gradientBorder 3s ease infinite',
          maxWidth: '1200px',
          width: '90%',
          maxHeight: '85vh',
          boxShadow: '0 0 30px rgba(0, 255, 255, 0.4), 0 0 60px rgba(255, 0, 255, 0.3), 0 0 90px rgba(0, 255, 255, 0.2)'
        }}
      >
        <style>{`
          @keyframes gradientBorder {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
        `}</style>
        <div 
          className="spotify-full-view-modal"
          style={{
            background: 'rgba(20, 20, 35, 0.98)',
            borderRadius: '21px',
            width: '100%',
            maxHeight: 'calc(85vh - 6px)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
        {/* Header */}
        <div style={{
          padding: '1.5rem 2rem',
          borderBottom: '2px solid rgba(0, 255, 255, 0.2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <h2 style={{ margin: 0, fontSize: '1.5rem', color: 'white' }}>
            🎵 Spotify Playlists - Full View
          </h2>
          <button 
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: 'rgba(255, 255, 255, 0.7)',
              fontSize: '2rem',
              cursor: 'pointer',
              padding: '0',
              lineHeight: '1'
            }}
          >
            ✕
          </button>
        </div>

        {/* Search Bar */}
        <div style={{ padding: '1rem 2rem' }}>
          <input
            type="text"
            placeholder="Search playlists..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: '100%',
              padding: '0.75rem 1rem',
              background: 'rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(0, 255, 255, 0.3)',
              borderRadius: '8px',
              color: 'white',
              fontSize: '1rem'
            }}
          />
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflow: 'auto', padding: '0 2rem 2rem' }}>
          {loading && <p style={{ textAlign: 'center', color: 'white' }}>Loading...</p>}

          {!loading && !selectedPlaylist && (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                gap: '1.5rem'
              }}
            >
              {filteredPlaylists.map((playlist, index) => (
                <div
                  key={playlist.id}
                  onClick={() => handlePlaylistClick(playlist)}
                  style={{
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '2px solid rgba(0, 255, 255, 0.3)',
                    borderRadius: '12px',
                    padding: '1rem',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.75rem'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.background = 'rgba(0, 255, 255, 0.1)';
                    e.currentTarget.style.transform = 'translateY(-4px)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  {playlist.images && playlist.images.length > 0 && (
                    <img
                      src={playlist.images[0].url}
                      alt={playlist.name}
                      style={{
                        width: '100%',
                        aspectRatio: '1',
                        objectFit: 'cover',
                        borderRadius: '8px'
                      }}
                    />
                  )}
                  <div>
                    <h3 style={{
                      margin: '0 0 0.25rem 0',
                      fontSize: '1rem',
                      fontWeight: '600',
                      color: 'white',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {playlist.name}
                    </h3>
                    <p style={{
                      margin: 0,
                      fontSize: '0.85rem',
                      color: 'rgba(255, 255, 255, 0.7)'
                    }}>
                      {playlist.tracks_total} tracks
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Playlist Detail View */}
          {!loading && selectedPlaylist && (
            <div>
              <button
                onClick={closeDetailView}
                style={{
                  marginBottom: '1rem',
                  padding: '0.5rem 1rem',
                  background: 'rgba(0, 255, 255, 0.1)',
                  border: '1px solid rgba(0, 255, 255, 0.3)',
                  borderRadius: '6px',
                  color: '#00ffff',
                  cursor: 'pointer',
                  fontSize: '0.9rem'
                }}
              >
                ← Back to Playlists
              </button>

              <div style={{
                display: 'flex',
                gap: '2rem',
                marginBottom: '2rem',
                alignItems: 'flex-start'
              }}>
                {selectedPlaylist.images && selectedPlaylist.images.length > 0 && (
                  <img
                    src={selectedPlaylist.images[0].url}
                    alt={selectedPlaylist.name}
                    style={{
                      width: '200px',
                      height: '200px',
                      objectFit: 'cover',
                      borderRadius: '12px',
                      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)'
                    }}
                  />
                )}
                <div>
                  <h2 style={{ margin: '0 0 0.5rem 0', color: 'white' }}>
                    {selectedPlaylist.name}
                  </h2>
                  <p style={{ margin: '0 0 1rem 0', color: 'rgba(255, 255, 255, 0.7)' }}>
                    {selectedPlaylist.description || 'No description'}
                  </p>
                  <p style={{ margin: 0, color: 'rgba(0, 255, 255, 0.9)', fontWeight: '600' }}>
                    {selectedPlaylist.tracks_total} tracks
                  </p>
                </div>
              </div>

              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                marginBottom: '1rem'
              }}>
                <h3 style={{ margin: 0, color: 'white' }}>Tracks:</h3>
                {/* Show crate context if adding to crate */}
                {isAddingToCrate && targetCrateName && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem'
                  }}>
                    <div style={{
                      padding: '0.5rem 1rem',
                      background: 'rgba(0, 255, 255, 0.1)',
                      border: '1px solid rgba(0, 255, 255, 0.3)',
                      borderRadius: '6px',
                      fontSize: '0.85rem',
                      color: '#00ffff'
                    }}>
                      📦 Adding to: <strong>{targetCrateName}</strong>
                    </div>
                    <button
                      onClick={onReturnToCrate}
                      style={{
                        padding: '0.5rem 1rem',
                        background: 'transparent',
                        border: '2px solid rgba(255, 0, 255, 0.6)',
                        borderRadius: '6px',
                        fontSize: '0.85rem',
                        color: '#ff00ff',
                        fontWeight: 600,
                        cursor: 'pointer',
                        boxShadow: '0 0 15px rgba(255, 0, 255, 0.3)',
                        transition: 'all 0.2s',
                        transform: 'translateY(0)'
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.background = 'rgba(255, 0, 255, 0.1)';
                        e.currentTarget.style.boxShadow = '0 6px 20px rgba(255, 0, 255, 0.4)';
                        e.currentTarget.style.transform = 'translateY(-3px)';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.background = 'transparent';
                        e.currentTarget.style.boxShadow = '0 0 15px rgba(255, 0, 255, 0.3)';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }}
                    >
                      ← Return to Crate
                    </button>
                  </div>
                )}
                {/* Show playlist context if adding to playlist */}
                {activePlaylistForAdding && !isAddingToCrate && (
                  <div style={{
                    padding: '0.5rem 1rem',
                    background: 'rgba(0, 255, 255, 0.1)',
                    border: '1px solid rgba(0, 255, 255, 0.3)',
                    borderRadius: '6px',
                    fontSize: '0.85rem',
                    color: '#00ffff'
                  }}>
                    Adding to: <strong>{activePlaylistForAdding.name}</strong>
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {playlistTracks.map((track, index) => (
                  <div
                    key={track.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '1rem',
                      padding: '0.75rem',
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '8px',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseOver={(e) => {
                      if (activePlaylistForAdding) {
                        e.currentTarget.style.background = 'rgba(0, 255, 255, 0.05)';
                        e.currentTarget.style.borderColor = 'rgba(0, 255, 255, 0.3)';
                      }
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                      e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                    }}
                  >
                    <span style={{ color: 'rgba(255, 255, 255, 0.5)', minWidth: '30px' }}>
                      {index + 1}
                    </span>
                    {track.album_art && (
                      <img
                        src={track.album_art}
                        alt={track.name}
                        style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '4px'
                        }}
                      />
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: '0.95rem',
                        fontWeight: '600',
                        color: 'white',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {track.name}
                      </div>
                      <div style={{
                        fontSize: '0.85rem',
                        color: 'rgba(255, 255, 255, 0.7)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {track.artist}
                      </div>
                    </div>
                    <span style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.85rem', minWidth: '50px' }}>
                      {Math.floor(track.duration_ms / 60000)}:{String(Math.floor((track.duration_ms % 60000) / 1000)).padStart(2, '0')}
                    </span>
                    {/* Add to Crate Button - Show when in crate mode */}
                    {isAddingToCrate && targetCrateId && (
                      <button
                        onClick={() => onTrackAddedToCrate && onTrackAddedToCrate(track)}
                        disabled={addingTrackId === track.id}
                        style={{
                          background: addingTrackId === track.id 
                            ? 'rgba(0, 255, 255, 0.2)' 
                            : 'rgba(0, 255, 255, 0.1)',
                          border: '1px solid rgba(0, 255, 255, 0.4)',
                          borderRadius: '6px',
                          padding: '0.5rem 0.75rem',
                          color: '#00ffff',
                          cursor: addingTrackId === track.id ? 'not-allowed' : 'pointer',
                          fontSize: '1.2rem',
                          transition: 'all 0.2s ease',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.25rem',
                          opacity: addingTrackId === track.id ? 0.5 : 1
                        }}
                        onMouseOver={(e) => {
                          if (addingTrackId !== track.id) {
                            e.currentTarget.style.background = 'rgba(0, 255, 255, 0.2)';
                            e.currentTarget.style.transform = 'scale(1.1)';
                          }
                        }}
                        onMouseOut={(e) => {
                          if (addingTrackId !== track.id) {
                            e.currentTarget.style.background = 'rgba(0, 255, 255, 0.1)';
                            e.currentTarget.style.transform = 'scale(1)';
                          }
                        }}
                        title={`Add to ${targetCrateName}`}
                      >
                        📦
                      </button>
                    )}
                    {/* Add to Playlist Button - Show when adding to playlist */}
                    {activePlaylistForAdding && !isAddingToCrate && (
                      <button
                        onClick={() => handleAddTrack(track)}
                        disabled={addingTrackId === track.id}
                        style={{
                          background: addingTrackId === track.id 
                            ? 'rgba(0, 255, 255, 0.2)' 
                            : 'rgba(0, 255, 255, 0.1)',
                          border: '1px solid rgba(0, 255, 255, 0.4)',
                          borderRadius: '6px',
                          padding: '0.5rem 0.75rem',
                          color: '#00ffff',
                          cursor: addingTrackId === track.id ? 'not-allowed' : 'pointer',
                          fontSize: '1.2rem',
                          transition: 'all 0.2s ease',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.25rem',
                          opacity: addingTrackId === track.id ? 0.5 : 1
                        }}
                        onMouseOver={(e) => {
                          if (addingTrackId !== track.id) {
                            e.currentTarget.style.background = 'rgba(0, 255, 255, 0.2)';
                            e.currentTarget.style.transform = 'scale(1.1)';
                          }
                        }}
                        onMouseOut={(e) => {
                          if (addingTrackId !== track.id) {
                            e.currentTarget.style.background = 'rgba(0, 255, 255, 0.1)';
                            e.currentTarget.style.transform = 'scale(1)';
                          }
                        }}
                        title={addingTrackId === track.id ? 'Adding...' : `Add to ${activePlaylistForAdding.name} - Will play via Spotify Premium`}
                      >
                        {addingTrackId === track.id ? '⏳' : '🎧'}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      </div>
    </div>
  );
};

export default SpotifyFullViewModal;
